/**
 * Convenio.
 *
 * La pantalla donde el producto enseña su tesis: el convenio solo puede
 * APRETAR la ley. Si el admin escribe 200 h de horas extra, el backend guarda
 * 80 y aquí se lo contamos como lo que es — protección, no error. El diseño
 * pide "que se note que le estás protegiendo".
 *
 * Multi-convenio: un tenant puede tener varios (turnos, centros, colectivos).
 * El selector cambia de convenio; el editor bindea el seleccionado. Los topes
 * legales (incluidas las noches) los manda el backend por jurisdicción — nunca
 * se hardcodean aquí, y un `null` significa "esa jurisdicción no fija tope",
 * no "sin límite matemático" (por eso nunca se usa `?? Infinity`).
 *
 * Orquestador: mantiene todo el estado (datos + campos del formulario) y las
 * llamadas al API; `PolicySelector` y `PolicyEditor` (carpeta `./policy/`)
 * son presentacionales y reciben callbacks.
 */
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import { PolicySelector } from './policy/PolicySelector';
import { PolicyEditor } from './policy/PolicyEditor';
import {
  getWorkPolicies,
  createWorkPolicy,
  deleteWorkPolicy,
  updateWorkPolicy,
  WorkPolicyView,
} from '../../services/api/workPolicies';
import { apiMessage } from '../../services/api/http';

export function PolicyAdmin() {
  const [policies, setPolicies] = useState<WorkPolicyView[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [overtime, setOvertime] = useState('');
  const [vacation, setVacation] = useState('');
  const [nightStart, setNightStart] = useState('22:00');
  const [nightEnd, setNightEnd] = useState('06:00');
  const [lunchAuto, setLunchAuto] = useState(false);
  const [lunchMins, setLunchMins] = useState('60');
  const [saving, setSaving] = useState(false);

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [creatingBusy, setCreatingBusy] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const policy = policies.find((p) => p.id === selectedId) ?? null;

  const load = useCallback(async (preferId?: number) => {
    const list = await getWorkPolicies();
    setPolicies(list);
    const preferred = preferId != null ? list.find((x) => x.id === preferId) : undefined;
    const fallback = list.find((x) => x.is_default) ?? list[0];
    setSelectedId((preferred ?? fallback)?.id ?? null);
    setLoaded(true);
  }, []);

  useEffect(() => {
    load().catch(() => {
      setError('No se pudieron cargar los convenios');
      setLoaded(true);
    });
  }, [load]);

  // Rebindea el formulario cuando cambia el convenio SELECCIONADO (no en cada
  // tecla, ni cuando `save()` refresca el mismo convenio con otra referencia).
  useEffect(() => {
    if (!policy) return;
    setName(policy.name);
    // null = "sin tope" (ni convenio ni jurisdicción lo fijan): campo vacío,
    // jamás un número inventado por la pantalla.
    setOvertime(policy.max_overtime_annual == null ? '' : String(policy.max_overtime_annual));
    setVacation(String(policy.vacation_days_annual));
    setNightStart(policy.night_start);
    setNightEnd(policy.night_end);
    setLunchAuto(policy.lunch_auto_deduct);
    setLunchMins(String(policy.lunch_deduction_minutes));
    setSavedMsg(null);
    setConfirmDelete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy?.id]);

  if (!loaded) {
    return (
      <AdminLayout title="Convenio">
        {error ? (
          <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px] max-w-[560px]">{error}</p>
        ) : (
          <p className="text-ink-3 text-[14px]">Cargando…</p>
        )}
      </AdminLayout>
    );
  }

  // Vaciar el campo mandaba `max_overtime_annual: 0`, y 0 es un valor legal
  // (el backend sólo recorta hacia arriba): todo el convenio quedaba con techo
  // CERO —o sea, horas extra prohibidas a la plantilla entera— con un
  // "Guardado." en verde y sin una línea que lo dijera. Vacaciones sí tenía
  // red (0 < mínimo legal → se recorta a 30 y se anuncia); horas extra no,
  // porque 0 es válido. Vacío ahora significa "no lo toques": el campo NI
  // VIAJA en el PUT. Eso cierra además el round-trip del tope inventado: en
  // un tenant sin tope legal (base UE) el GET servía el 80 español y el eco
  // del formulario lo consolidaba en el convenio (r2b04[3]) — un eco de la
  // pantalla no puede fabricar ley.
  const n = parseInt(overtime || '0', 10) || 0;
  const legalOvertimeCap = policy?.legal_max_overtime_annual ?? null;
  const over = legalOvertimeCap !== null && n > legalOvertimeCap;
  const atOvertimeCap = legalOvertimeCap !== null && n === legalOvertimeCap;
  const vDays = parseInt(vacation || '0', 10) || 0;
  const vUnder = policy != null && vDays < policy.legal_min_vacation_days;

  const submitCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreatingBusy(true);
    setError(null);
    try {
      const created = await createWorkPolicy(trimmed);
      setCreating(false);
      setNewName('');
      await load(created.id);
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo crear el convenio'));
    } finally {
      setCreatingBusy(false);
    }
  };

  const handleDeleteClick = () => {
    if (!policy || policy.is_default) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    void doDelete();
  };

  const doDelete = async () => {
    if (!policy) return;
    setDeleting(true);
    setError(null);
    setSavedMsg(null);
    try {
      await deleteWorkPolicy(policy.id);
      setConfirmDelete(false);
      await load();
    } catch (e: unknown) {
      // 409: convenio por defecto, o con trabajadores asignados — el backend
      // ya explica cuál de los dos es; se muestra tal cual, sin traducir.
      setError(apiMessage(e, 'No se pudo borrar el convenio'));
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const save = async () => {
    if (!policy) return;
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      const updated = await updateWorkPolicy(policy.id, {
        name,
        // `annual_hours` NO viaja: ningún motor lo consume (el propio
        // endpoint lo declara parámetro inerte) y el campo dejó de ser
        // editable (se muestra como dato, no como control). Mandar el eco
        // del GET materializaba el default en `rules` en el primer guardado
        // de cualquier otro campo — el round-trip que fabricaba ley,
        // reabierto para este campo (ronda 4, r2b07[1]).
        // Horas extra: sólo viaja si el admin escribió un número. Vacío =
        // "no lo toques" (ni 0 ni el eco de un tope que nadie fijó).
        ...(overtime.trim() !== '' && { max_overtime_annual: n }),
        vacation_days_annual: vDays,
        // Las horas de la franja nocturna son inputs type="time": vaciarlos
        // dejaba viajar `''` y el patrón HH:MM del backend mataba el
        // guardado ENTERO con un 422 que la pantalla mostraba como "Error
        // del servidor". Vacío = "no lo toques", como las extra (r2b20[9]).
        ...(nightStart !== '' && { night_start: nightStart }),
        ...(nightEnd !== '' && { night_end: nightEnd }),
        lunch_auto_deduct: lunchAuto,
        // El backend rechaza más de 240 con un 422 que tumba el guardado
        // ENTERO —nombre, noche y comida del tirón—. Se acota aquí: nunca se
        // manda un valor que el backend vaya a rechazar.
        lunch_deduction_minutes: Math.min(240, parseInt(lunchMins || '0', 10) || 0),
      });
      setPolicies((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setName(updated.name);
      setOvertime(updated.max_overtime_annual == null ? '' : String(updated.max_overtime_annual));
      setVacation(String(updated.vacation_days_annual));
      setNightStart(updated.night_start);
      setNightEnd(updated.night_end);
      setLunchAuto(updated.lunch_auto_deduct);
      setLunchMins(String(updated.lunch_deduction_minutes));
      setSavedMsg(
        updated.clamped.length > 0
          ? 'Guardado — con los valores ajustados al límite legal.'
          : 'Guardado.'
      );
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  const overtimeMsg = legalOvertimeCap === null
    ? 'Tu jurisdicción no fija tope legal para las horas extra: el convenio decide libremente.'
    : over
      ? `Te estamos protegiendo: el techo legal son ${legalOvertimeCap} h/año (art. 35.2 ET). Al guardar se ajusta.`
      : atOvertimeCap
        ? 'En el máximo legal. Puedes bajarlo por convenio, no subirlo.'
        : 'Dentro del límite. El convenio puede apretarlo.';

  return (
    <AdminLayout title="Convenio" subtitle={policy?.name}>
      <div className="max-w-[820px] space-y-4">
        {error && <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px]">{error}</p>}

        <PolicySelector
          policies={policies}
          selectedId={selectedId}
          policy={policy}
          onSelect={setSelectedId}
          creating={creating}
          onToggleCreating={() => {
            setCreating((v) => !v);
            setNewName('');
          }}
          newName={newName}
          onNewNameChange={setNewName}
          onSubmitCreate={() => void submitCreate()}
          creatingBusy={creatingBusy}
          confirmDelete={confirmDelete}
          deleting={deleting}
          onDeleteClick={handleDeleteClick}
          onCancelDelete={() => setConfirmDelete(false)}
        />

        {policy && (
          <PolicyEditor
            policy={policy}
            name={name}
            onNameChange={setName}
            overtime={overtime}
            onOvertimeChange={(raw) => setOvertime(raw.replace(/[^0-9]/g, '').slice(0, 4))}
            legalOvertimeCap={legalOvertimeCap}
            over={over}
            overtimeMsg={overtimeMsg}
            vacation={vacation}
            onVacationChange={(raw) => setVacation(raw.replace(/[^0-9]/g, '').slice(0, 3))}
            vUnder={vUnder}
            nightStart={nightStart}
            onNightStartChange={setNightStart}
            nightEnd={nightEnd}
            onNightEndChange={setNightEnd}
            lunchAuto={lunchAuto}
            onToggleLunchAuto={() => setLunchAuto((v) => !v)}
            lunchMins={lunchMins}
            onLunchMinsChange={(raw) => setLunchMins(raw.replace(/[^0-9]/g, '').slice(0, 3))}
            saving={saving}
            savedMsg={savedMsg}
            onSave={() => void save()}
          />
        )}
      </div>
    </AdminLayout>
  );
}
